import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Smartphone, Download } from 'lucide-react';

const IntroSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const sectionRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Start playing video when section becomes visible
                    if (videoRef.current && videoLoaded) {
                        videoRef.current.play().catch(console.error);
                    }
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, [videoLoaded]);

    return (
        <section
            ref={sectionRef}
            className="w-full bg-gray-50 pt-20 overflow-hidden"
            aria-labelledby="carrier-app-section-title"
            role="region"
        >
            {/* Top Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
                <div className="text-center">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <h2
                            id="carrier-app-section-title"
                            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight mb-6"
                        >
                            Maximize Your Fleet Revenue – Smart Transportation Solutions{' '}
                            <span className="text-[#F05A28]">For Partners!</span>
                        </h2>
                    </div>

                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.2s' }}
                    >
                        <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
                            <span className="text-[#F05A28] font-semibold">RoadExpress Partner App</span> empowers transporters and carriers with cutting-edge technology to grow their business efficiently. Access premium load opportunities, manage your entire fleet seamlessly, and maximize profits with our transparent bidding system. From real-time fleet tracking to instant payment updates, everything you need to run a successful transportation business is right at your fingertips. Join thousands of successful carriers who trust RoadExpress for consistent loads and reliable partnerships.
                        </p>
                    </div>
                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "MobileApplication",
                        "name": "RoadExpress Partner App",
                        "description": "Smart transportation solution for carriers and transporters with fleet management, load bidding, and real-time tracking",
                        "applicationCategory": "BusinessApplication",
                        "operatingSystem": "iOS, Android",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "author": {
                            "@type": "Organization",
                            "name": "RoadExpress"
                        },
                        "featureList": [
                            "Load bidding and booking",
                            "Real-time fleet tracking",
                            "Payment management",
                            "Trip status monitoring",
                            "AI-powered route optimization"
                        ]
                    })
                }}
            />
        </section>
    );
};

export default IntroSection;